import React, { useEffect, useState } from 'react';
import { Edit, Trash2, Check, X, Plus, Search, User, Users } from 'lucide-react';
import { useFossasSepticas } from '../context/FossasSepticasProvider';
import { formatCpfCnpj, formatPhone } from '../utils/formatters';
import { toast } from 'react-hot-toast';
import supabase from '../../../config/supabaseClient';
import { 
  applyIconOnlyStyle,
  getButtonStyle
} from '../utils/buttonStyles';
import {
  getSelectStyle,
  getInputStyle,
  formGroupStyle,
  labelStyle
} from '../utils/formStyles';
import {
  getCardStyle,
  getCardGridStyle,
  cardTitleStyle,
  cardSubtextStyle,
  cardInfoLineStyle,
  cardValueStyle,
  getCardTitleColor
} from '../utils/cardStyles';
import {
  listContainerStyle,
  loadingIndicatorStyle,
  spinnerStyle,
  emptyListStyle,
  emptyListIconStyle,
  pageHeaderStyle,
  obraCardStyle,
  obraCardHeaderStyle,
  obraCardTitleStyle,
  obraCardStatusStyle,
  obraCardBodyStyle,
  obraCardInfoLineStyle,
  obraCardLabelStyle,
  obraCardValueStyle,
  obraCardFooterStyle,
  getFiltersContainerStyle,
  searchFieldContainerStyle,
  searchIconStyle,
  paginationContainerStyle,
  getInfoBadgeStyle
} from '../utils/listStyles';

/**
 * Componente para gerenciamento de agentes de campo
 */
const GerenciadorAgentes = () => {
  const { 
    agentes, 
    carregarAgentes, 
    salvarAgente, 
    loading 
  } = useFossasSepticas();

  const [showForm, setShowForm] = useState(false);
  const [editingAgente, setEditingAgente] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    telefone: '',
    email: '',
    ativo: true
  });
  const [errors, setErrors] = useState({});

  // Carregar agentes ao montar o componente
  useEffect(() => {
    carregarAgentes();
  }, [carregarAgentes]);

  // Abrir formulário para novo agente
  const handleNovoAgente = () => {
    setFormData({
      nome: '',
      cpf: '',
      telefone: '',
      email: '',
      ativo: true
    });
    setEditingAgente(null);
    setShowForm(true);
    setErrors({});
  };

  // Abrir formulário para editar agente
  const handleEditar = (agente) => {
    // Extrair apenas os campos necessários para o formulário
    // Isso evita problemas com campos adicionados pelo contexto
    setFormData({
      id: agente.id,
      nome: agente.nome,
      cpf: agente.cpf,
      telefone: agente.telefone,
      email: agente.email || '',
      ativo: agente.ativo
    });
    setEditingAgente(agente);
    setShowForm(true);
    setErrors({});
  };

  // Cancelar edição
  const handleCancelar = () => {
    setShowForm(false);
    setEditingAgente(null);
  };

  // Atualizar estado do formulário
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Validar formulário
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }
    
    if (!formData.cpf.trim()) {
      newErrors.cpf = 'CPF é obrigatório';
    }
    
    if (!formData.telefone.trim()) {
      newErrors.telefone = 'Telefone é obrigatório';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Enviar formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const success = await salvarAgente(formData);
      
      if (success) {
        setShowForm(false);
        setEditingAgente(null);
      }
    }
  };

  // Alternar status do agente (ativo/inativo)
  const handleToggleStatus = async (agente) => {
    // Extrair apenas os campos necessários para atualização
    // Isso evita enviar propriedades que não são colunas na tabela
    const { id, nome, cpf, telefone, email, ativo } = agente;
    
    const updatedAgente = {
      id,
      nome,
      cpf,
      telefone,
      email,
      ativo: !ativo
    };
    
    await salvarAgente(updatedAgente);
  };

  // Excluir agente
  const handleExcluir = async (agenteId) => {
    if (window.confirm('Tem certeza que deseja excluir este agente? Esta ação não pode ser desfeita.')) {
      try {
        const { error } = await supabase
          .from('agentes')
          .delete()
          .eq('id', agenteId);
        
        if (error) throw error;
        
        toast.success('Agente excluído com sucesso');
        carregarAgentes();
      } catch (error) {
        console.error('Erro ao excluir agente:', error);
        toast.error('Erro ao excluir agente');
      }
    }
  };
  
  // Filtrar agentes
  const agentesFiltrados = agentes.filter(agente => {
    // Filtrar por termo de busca
    if (searchTerm && !agente.nome.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !agente.cpf.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !(agente.email && agente.email.toLowerCase().includes(searchTerm.toLowerCase()))) {
      return false;
    }
    
    // Filtrar por status
    if (statusFilter === 'ativo' && !agente.ativo) {
      return false;
    }
    if (statusFilter === 'inativo' && agente.ativo) {
      return false;
    }
    
    return true;
  });
  
  // Calcular agentes para a página atual
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAgentes = agentesFiltrados.slice(indexOfFirstItem, indexOfLastItem);
  
  // Mudar de página
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Limpar filtros
  const limparFiltros = () => {
    setSearchTerm('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  return (
    <div>
      <div className="card">
        <div style={pageHeaderStyle}>
          <h2>Gerenciador de Agentes</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={handleNovoAgente}
              style={getButtonStyle('primary')}
            >
              <Plus size={16} />
              Novo Agente
            </button>
          </div>
        </div>
      
      {showForm && (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginTop: 0, marginBottom: '20px' }}>{editingAgente ? 'Editar Agente' : 'Novo Agente'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>Nome</label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  style={{ 
                    ...getInputStyle(), 
                    borderColor: errors.nome ? '#dc3545' : null 
                  }}
                  placeholder="Nome completo"
                />
                {errors.nome && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{errors.nome}</div>}
              </div>
              
              <div style={formGroupStyle}>
                <label style={labelStyle}>CPF</label>
                <input
                  type="text"
                  name="cpf"
                  value={formData.cpf}
                  onChange={handleChange}
                  style={{ 
                    ...getInputStyle(), 
                    borderColor: errors.cpf ? '#dc3545' : null 
                  }}
                  placeholder="000.000.000-00"
                />
                {errors.cpf && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{errors.cpf}</div>}
              </div>
              
              <div style={formGroupStyle}>
                <label style={labelStyle}>Telefone</label>
                <input
                  type="text"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  style={{ 
                    ...getInputStyle(), 
                    borderColor: errors.telefone ? '#dc3545' : null 
                  }}
                  placeholder="(00) 00000-0000"
                />
                {errors.telefone && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{errors.telefone}</div>}
              </div>
              
              <div style={formGroupStyle}>
                <label style={labelStyle}>Email (opcional)</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  style={getInputStyle()}
                  placeholder="email@exemplo.com"
                />
              </div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="ativo"
                  checked={formData.ativo}
                  onChange={handleChange}
                  style={{ marginRight: '8px' }}
                />
                Agente ativo
              </label>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button 
                type="button" 
                onClick={handleCancelar}
                style={getButtonStyle('secondary')}
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                disabled={loading.agentes}
                style={loading.agentes ? 
                  { ...getButtonStyle('primary'), opacity: 0.7, cursor: 'not-allowed' } : 
                  getButtonStyle('primary')
                }
              >
                {loading.agentes ? 'Salvando...' : editingAgente ? 'Atualizar' : 'Cadastrar'}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Cards de Totalizadores */}
      <div style={getCardGridStyle(3)}>
        {/* Card Total */}
        <div style={getCardStyle('neutral')}>
          <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('neutral') }}>Total de Agentes</h3>
          <div style={cardValueStyle}>
            {agentes.length}
          </div>
          <div style={cardSubtextStyle}>Agentes cadastrados</div>
        </div>
        
        {/* Card Ativos */}
        <div style={getCardStyle('success')}>
          <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('success') }}>Agentes Ativos</h3>
          <div style={cardValueStyle}>
            {agentes.filter(a => a.ativo).length}
          </div>
          <div style={cardSubtextStyle}>Agentes disponíveis para visitas</div>
        </div>
        
        {/* Card Inativos */}
        <div style={getCardStyle('danger')}>
          <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('danger') }}>Agentes Inativos</h3>
          <div style={cardValueStyle}>
            {agentes.filter(a => !a.ativo).length}
          </div>
          <div style={cardSubtextStyle}>Agentes temporariamente indisponíveis</div>
        </div>
      </div>
      
      {/* Filtros */}
      <div style={getFiltersContainerStyle(2)}>
        <div style={{ ...searchFieldContainerStyle, ...formGroupStyle }}>
          <label style={labelStyle}>Buscar</label>
          <div style={{ position: 'relative' }}>
            <Search size={18} style={searchIconStyle} />
            <input
              type="text"
              placeholder="Buscar por nome, CPF ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ 
                ...getInputStyle(), 
                paddingLeft: '40px'
              }}
            />
          </div>
        </div>
        
        <div style={formGroupStyle}>
          <label style={labelStyle}>Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={getSelectStyle()}
          >
            <option value="">Todos os status</option>
            <option value="ativo">Ativos</option>
            <option value="inativo">Inativos</option>
          </select>
        </div>
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        <button 
          onClick={limparFiltros}
          style={getButtonStyle('secondary')}
        >
          Limpar Filtros
        </button>
      </div>

      {loading.agentes && !showForm ? (
        <div style={loadingIndicatorStyle}>
          <div style={spinnerStyle}></div>
          <p>Carregando agentes...</p>
        </div>
      ) : (
        <>
          {agentesFiltrados.length === 0 ? (
            <div style={emptyListStyle}>
              <User size={48} style={emptyListIconStyle} />
              <h3>Nenhum agente encontrado</h3>
              <p>Não foram encontrados agentes com os filtros aplicados.</p>
              <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                {searchTerm || statusFilter ? (
                  <button 
                    onClick={limparFiltros}
                    style={getButtonStyle('secondary')}
                  >
                    Limpar Filtros
                  </button>
                ) : null}
                <button 
                  onClick={handleNovoAgente}
                  style={getButtonStyle('primary')}
                >
                  <Plus size={16} />
                  Cadastrar {agentes.length === 0 ? 'Primeiro ' : ''}Agente
                </button>
              </div>
            </div>
          ) : (
            <div style={listContainerStyle}>
              {currentAgentes.map(agente => (
                <div key={agente.id} style={{
                  ...obraCardStyle,
                  backgroundColor: !agente.ativo ? '#f8f9fa' : 'white'
                }}>
                  {/* Cabeçalho do card */}
                  <div style={obraCardHeaderStyle}>
                    <h3 style={obraCardTitleStyle}>{agente.nome}</h3>
                    <span style={{ 
                      ...obraCardStatusStyle, 
                      backgroundColor: agente.ativo ? '#28a745' : '#6c757d',
                      color: 'white'
                    }}>
                      {agente.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  
                  {/* Corpo do card */}
                  <div style={obraCardBodyStyle}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      {/* Coluna da esquerda - Informações principais */}
                      <div>
                        <div style={obraCardInfoLineStyle}>
                          <span style={obraCardLabelStyle}>CPF:</span>
                          <span style={obraCardValueStyle}>{formatCpfCnpj(agente.cpf)}</span>
                        </div>
                        <div style={obraCardInfoLineStyle}>
                          <span style={obraCardLabelStyle}>Telefone:</span>
                          <span style={obraCardValueStyle}>{formatPhone(agente.telefone)}</span>
                        </div>
                        <div style={obraCardInfoLineStyle}>
                          <span style={obraCardLabelStyle}>Email:</span>
                          <span style={obraCardValueStyle}>{agente.email || '-'}</span>
                        </div>
                      </div>
                      
                      {/* Coluna da direita - Detalhes técnicos */}
                      <div>
                        <div style={getInfoBadgeStyle('#e8f5e9', '#2e7d32')}>
                          <Users size={16} style={{ color: '#2e7d32' }} />
                          <span>Propriedades atribuídas: {agente.propriedades?.length || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Rodapé do card com botões */}
                  <div style={obraCardFooterStyle}>
                    <button 
                      onClick={() => handleEditar(agente)}
                      style={getButtonStyle('secondary', { small: true })}
                    >
                      <Edit size={14} />
                      Editar
                    </button>
                    <button 
                      onClick={() => handleToggleStatus(agente)}
                      style={getButtonStyle('info', { small: true })}
                    >
                      {agente.ativo ? (
                        <>
                          <X size={14} />
                          Desativar
                        </>
                      ) : (
                        <>
                          <Check size={14} />
                          Ativar
                        </>
                      )}
                    </button>
                    <button 
                      onClick={() => handleExcluir(agente.id)}
                      style={getButtonStyle('danger', { small: true })}
                    >
                      <Trash2 size={14} />
                      Excluir
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Paginação */}
          {agentesFiltrados.length > itemsPerPage && (
            <div style={paginationContainerStyle}>
              {Array.from({ length: Math.ceil(agentesFiltrados.length / itemsPerPage) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => paginate(index + 1)}
                  style={{
                    ...applyIconOnlyStyle(
                      getButtonStyle(currentPage === index + 1 ? 'primary' : 'secondary')
                    ),
                    width: '32px',
                    height: '32px',
                    margin: '0 5px',
                    padding: '0',
                    justifyContent: 'center'
                  }}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          )}
        </>
      )}
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  </div>
  );
};

export default GerenciadorAgentes;